import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { getMousePosition } from '@modules/creator/components/special-block/lightbox/lightbox-helpers';
import { LightboxService } from '@modules/creator/components/special-block/lightbox/services/lightbox.service';
import { getValueFromPercentageOf, whatPercentOf } from '@shared/helpers';
import { getStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';
import { Subscription } from 'rxjs';

const startData = {
  scale: 1,
  translate: {x: 0, y: 0},
  oldTranslate: {x: 0, y: 0},
};

@Component({
  selector: 'app-lightbox-preview-image',
  templateUrl: './lightbox-preview-image.component.html',
  styleUrls: ['./lightbox-preview-image.component.scss'],
  standalone: false,
})
export class LightboxPreviewImageComponent implements OnInit {
  @Input() block: Block;
  @Input() photo: Block;
  @Output() isZoomedImage = new EventEmitter();
  @ViewChild('container') container: ElementRef;
  zoom = Object.assign({}, startData);
  isMouseDownOnImage = false;
  isZooming = false;
  imgSrc = '';
  private isZoomed = false;
  private mouseDownTime: number;
  private mouseUpTime: number;
  private mouseDownPos = {x: 0, y: 0};
  private mouseUpPos = {x: 0, y: 0};
  private mediaQuerySubscription: Subscription;
  private blockStyles: MixedObject = {};

  constructor(
    private globalService: GlobalService,
    lightBoxService: LightboxService,
  ) {
    lightBoxService.openPreviewAtIndex.subscribe(_ => {
      this.imgSrc = this.imageUrl;
      this.resetStyles();
    });
    this.mediaQuerySubscription = globalService.mediaQuery.subscribe(mediaQuery => {
      this.generateBlockStyles(mediaQuery);
    });
  }

  get imageUrl(): string {
    return (this.photo.media ?? this.photo.image)?.urls.full;
  }

  get layerStyle() {
    return {
      cursor: this.isZoomed ? 'grabbing' : (this.canZoomImage ? 'zoom-in' : 'default'),
    };
  }

  get imageStyle() {
    const additionalCss: any = {
      minHeight: '100%',
    };
    if (this.isZoomed) {
      const img = this.imageNode;
      additionalCss.maxWidth = img.naturalWidth + 'px';
      additionalCss.maxHeight = img.naturalHeight + 'px';
      const {x, y} = this.zoom.translate;
      additionalCss.transform = `translate(${x}px, ${y}px)`;
    }
    const {
      basic: {width, height, minHeight, objectFit},
    } = this.blockStyles;

    return {
      width,
      height,
      minHeight,
      objectFit,
      ...additionalCss,
    };
  }

  get imageNode(): HTMLImageElement {
    return this.container?.nativeElement.querySelector('img.image') as HTMLImageElement;
  }

  private _dimensionsBig = {w: 0, h: 0};

  private get dimensionsBig() {
    if (!this._dimensionsBig.w || !this._dimensionsBig.h) {
      const img = this.imageNode;
      this._dimensionsBig.w = img.naturalWidth;
      this._dimensionsBig.h = img.naturalHeight;
    }
    return this._dimensionsBig;
  }

  private get canZoomImage(): boolean {
    const imageNode = this.imageNode;
    if (!imageNode) {
      return false;
    }
    const imgRect = imageNode.getBoundingClientRect();

    return !(
      (Math.floor(imgRect.width) > Math.floor(imageNode.naturalWidth))
      && (Math.floor(imgRect.height) > Math.floor(imageNode.naturalHeight))
    );
  }

  ngOnInit() {
    this.generateBlockStyles(this.globalService.mediaQuery.value);
  }

  @HostListener('document:mouseout', ['$event'])
  documentMouseOut($event) {
    this.mouseUp($event);
  }

  mouseMove($event) {
    if (!this.isMouseDownOnImage || !this.isZoomed) {
      return;
    }

    const mouse = getMousePosition($event);
    // jeśli mamy zoom to przesuwamy obrazek
    const x = this.zoom.oldTranslate.x + (mouse.x - this.mouseDownPos.x);
    const y = this.zoom.oldTranslate.y + (mouse.y - this.mouseDownPos.y);
    this.setTranslate(x, y);
  }

  mouseDown($event) {
    this.mouseDownTime = new Date().getTime();
    this.isMouseDownOnImage = true;
    this.mouseDownPos = getMousePosition($event);
  }

  mouseUp($event) {
    this.mouseUpTime = new Date().getTime();
    this.isMouseDownOnImage = false;
    this.zoom.oldTranslate = this.zoom.translate;
    this.mouseUpPos = getMousePosition($event);
  }

  onImageClick($event) {
    this.mouseDownPos = getMousePosition($event);
    /*
    * robimy tak dlatego, że angular jako kliknięcie traktuje też dłuższe przytrzymanie przycisku myszy
    * więc nie reagujemy, jeśli jest to dłuższe przytrzymanie
    * lub krótkotrwałe przesuniecie o ponad 5px
     */
    if (this.mouseUpTime - this.mouseDownTime <= 300
      && Math.abs(this.mouseDownPos.x - this.mouseUpPos.x) <= 5
      && Math.abs(this.mouseDownPos.y - this.mouseUpPos.y) <= 5
    ) {
      this.zoomImage();
    }
  }

  private generateBlockStyles(mediaQuery: MediaQuery): void {
    const querySnakeCase = camelCase(mediaQuery.query);
    this.blockStyles = getStyles(this.photo?.styles || {}, querySnakeCase);
  }

  private zoomImage() {
    if (!this.isZoomed) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  private zoomIn() {
    const img = this.imageNode;
    const {width, height, left, top} = img.getBoundingClientRect();
    if (!this.canZoomImage) {
      return;
    }

    const relativePostTopOnImage = this.mouseDownPos.y - top;
    const relativePostLeftOnImage = this.mouseDownPos.x - left;

    // przeliczamy w jakim miejscu od góry i od lewej na zdjęciu procentowo kliknięto
    const percentTop = whatPercentOf(relativePostTopOnImage, height);
    const percentLeft = whatPercentOf(relativePostLeftOnImage, width);


    // w jakim punkcie w px zostało kliknięte
    // przeliczanie z procentów z pomniejszonych wymiarów do px względem oryginalnych
    const leftPx = getValueFromPercentageOf(percentLeft, img.naturalWidth);
    const topPx = getValueFromPercentageOf(percentTop, img.naturalHeight);

    this.setTranslate((-leftPx + (img.naturalWidth / 2)), (-topPx + (img.naturalHeight / 2)));
    this.zoom.oldTranslate = this.zoom.translate;
    this.setIsZoomed(true);
    this.isZooming = true;
    setTimeout(() => this.isZooming = false, 500);
  }

  private zoomOut() {
    this.setIsZoomed(false);
    this.isZooming = true;
    setTimeout(() => this.isZooming = false, 500);
    this.zoom.translate = {x: 0, y: 0};
  }

  private setIsZoomed(isZoomed: boolean) {
    this.isZoomed = isZoomed;
    this.isZoomedImage.emit(isZoomed);
  }

  private resetStyles() {
    this.zoom = Object.assign({}, startData);
    this.setIsZoomed(false);
  }

  private setTranslate(x: number, y: number): void {
    const {w, h} = this.dimensionsBig;
    const {innerHeight, innerWidth} = window;
    // dajemy Math.min ponieważ bez tego jeśli orginalne zdjecie ma np szerokość mniejszą niż ekran to przykleja sie do prawej strony
    const maxX = (w / 2) - (Math.min(innerWidth, w) / 2);
    const maxY = (h / 2) - (Math.min(innerHeight, h) / 2);
    if (x > maxX) {
      x = maxX;
    }
    if (x < -maxX) {
      x = -maxX;
    }
    if (y > maxY) {
      y = maxY;
    }
    if (y < -maxY) {
      y = -maxY;
    }
    this.zoom.translate = {x, y};
  }
}
